import api from './axios'; 

/**
 * Users service with new Firebase upload architecture
 * Now uses image_storage_id instead of FormData for profile images
 */
const usersRequests = {
    /**
     * Get all users
     * Response includes image_url (temporary presigned URL) and image_storage_id
     */
    getAll(){
        return api.get('/users')
    },
    
    /**
     * Get single user by ID
     * Response includes image_url (temporary presigned URL) and image_storage_id
     */
    getOne(id){
        return api.get(`/users/${id}`)
    },
    
    /**
     * Create new user with optional profile image
     * @param {Object} userData - User data
     * @param {string} userData.dni - User identification
     * @param {string} userData.company_name - Company name
     * @param {string} userData.assigned_person - Representative name
     * @param {string} userData.email - Email address
     * @param {string} userData.contact_phone - Phone number
     * @param {string} userData.country - Country code
     * @param {string} userData.location_one - State/Province
     * @param {string} userData.location_two - City/Canton
     * @param {number} [userData.image_storage_id] - Storage ID of uploaded profile image
     * @returns {Promise} API response with user data including image_url
     */
    save(userData){
        return api.post('/users', userData, {
            headers: {
                'Content-Type': 'application/json'
            }
        })
    },

    savePlatformAdmin(userData){
        return api.post('/users/platform-admins', userData, {
            headers: {
                'Content-Type': 'application/json'
            }
        })
    },
    
    /**
     * Update existing user
     * @param {string} id - User UUID
     * @param {Object} userData - Updated user data (same structure as save)
     * @returns {Promise} API response with updated user data including fresh image_url
     */
    update(id, userData){
        return api.patch(`/users/${id}`, userData, {
            headers: {
                'Content-Type': 'application/json'
            }
        })
    },
    
    /**
     * Update user password
     * @param {number} id - User ID
     * @param {Object} passwordData - Password update data
     * @param {string} passwordData.current_password - Current password
     * @param {string} passwordData.new_password - New password
     * @param {string} passwordData.confirm_password - Password confirmation
     */
    updatePassword(id, passwordData){
        return api.patch(`/users/${id}/password`, passwordData)
    },

    /**
     * Platform-owner reset of another user's password (global context, no X-Org-Id)
     */
    resetPassword(id, passwordData){
        return api.patch(`/users/${id}/password`, passwordData, { orgContext: 'global' })
    },

    /**
     * Org-admin reset of a selected-org member's password (tenant context, sends X-Org-Id)
     */
    resetOrgMemberPassword(id, passwordData){
        return api.patch(`/users/${id}/password`, passwordData, { orgContext: 'tenant' })
    },
    
    /**
     * Delete user
     * This will also handle deletion of associated profile image in Firebase
     */
    delete(id){
        return api.delete(`/users/${id}`)
    },
    
    /**
     * Refresh user image URL when expired
     * @param {string} id - User UUID
     * @returns {Promise} API response with fresh image_url
     */
    refreshImageUrl(id) {
        return api.get(`/users/${id}/refresh-image`)
    }
};

export default usersRequests;
